import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getCollaboratorsEcheance, getCollaboratorBalance } from "../../../api";
import PageHeader from "../../common/PageHeader/PageHeader";
import { BriefCaseIcon } from "../../common/Icons";
import EcheancesFilter from "./EcheancesFilter";
import "./Echeances.local.scss";
import { Tooltip } from "antd";
// import { CheckIcon } from "../../common/Icons";
import { CheckIcon, ChevronDown, ChevronUp } from "../../common/Icons";
import { ECHEANCES } from "../../../config";
import _ from "../../../i18n";
import moment from "moment";

const Table = ({
  token,
  organization,
  partner,
  partnerZoneId,
  year,
  collaboratorId,
  type,
  onTypeChange,
  balance_types,
  zoneId,
  bu,
  balanceRefresh,
  setBalanceRefresh,
  loggedAs,
  clients,
}) => {
  const [selectedBalanceTypes, setSelectedBalanceTypes] = useState(type);
  const [seles, setSeles] = useState(null);
  const [balances, setBalances] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [countBalances, setCountBalances] = useState(null);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [percentageByType, setPercentageByType] = useState([]);
  const [selectedEcheanceClosingDate, setSelectedEcheanceClosingDate] =
    useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showNotCompleted, setShowNotCompleted] = useState(true);
  const [ratesByGE, setRatesByGE] = useState(null);
  const [count, setCount] = useState(false);
  const [totalByField, setTotalByField] = useState({});

  const getDateParams = (type, effectiveZoneId, selectedDate) => {
    if (type === "TVA" && effectiveZoneId === "2") {
      return {
        closingDate: null,
        startDate: selectedDate?.startDate || null,
        endDate: selectedDate?.endDate || null,
      };
    } else {
      return {
        closingDate: selectedDate || null,
        startDate: null,
        endDate: null,
      };
    }
  };

  useEffect(() => {
    setSelectedBalanceTypes(type);
  }, [type]);

  useEffect(() => {
    if (balances && headers) {
      const allLines = [
        ...(balances.completed || []),
        ...(balances.not_completed || []),
      ].filter((b) => !b.client_name?.toLowerCase().includes("total"));
      const totals = {};

      const currentHeaders =
        (selectedBalanceTypes === "ISOC" || selectedBalanceTypes === "IPM") &&
        headers
          ? headers.map((header) => {
              if (header.children && header.children.length === 2) {
                return {
                  ...header,
                  children: [header.children[1], header.children[0]],
                };
              }
              return header;
            })
          : headers || [];

      currentHeaders.forEach((header) => {
        if (header.children) {
          header.children.forEach((child) => {
            totals[child.value] = allLines.filter(
              (b) => b[child.value] !== undefined
            ).length;
          });
        } else {
          totals[header.value] = allLines.filter(
            (b) => b[header.value] !== undefined
          ).length;
        }
      });

      setTotalByField(totals);
    }
  }, [balances, headers, selectedBalanceTypes]);

  let balancesQuery = useQuery(
    ["getCollaboratorBalance", token, organization, year, collaboratorId],
    async () => {
      if (token && organization) {
        const { closingDate, startDate, endDate } = getDateParams(
          selectedBalanceTypes,
          effectiveZoneId,
          selectedEcheanceClosingDate
        );
        try {
          const response = await getCollaboratorBalance(
            token,
            organization,
            partner,
            year,
            null,
            collaboratorId,
            zoneId,
            bu,
            selectedBalanceTypes,
            closingDate,
            null,
            startDate,
            endDate,
            clients
          );
          if (selectedBalanceTypes === "TVA" && zoneId === "2") {
            setBalances({
              global_total: response.data.data.response.global_total,
              finalized: response.data.data.response.finalized ?? [],
              in_progress: response.data.data.response.in_progress ?? [],
              waiting_documents:
                response.data.data.response.waiting_documents ?? [],
              planning_requested:
                response.data.data.response.planning_requested ?? [],
              planned: response.data.data.response.planned ?? [],
              to_plan: response.data.data.response.to_plan ?? [],
            });
          } else if (selectedBalanceTypes === "TVA" && zoneId === "1") {
            setBalances(response.data.data.response);

            setCountBalances(response.data.data.countData);
          } else {
            setBalances(response.data.data.response);

            setCountBalances(response.data.data.countData);
          }
          setHeaders(response.data.data.headers);
          setCount(response.data.data.response.global_total.total);
          setPercentageByType({
            [selectedBalanceTypes]: response.data.data.completionRate,
          });
          setRatesByGE(response.data.data.ratesByGE);
          setBalanceRefresh(false);
          setLoadingBalances(false);
          return response.data.data;
        } catch (e) {
          setBalances(null);
          setLoadingBalances(false);
          return null;
        } finally {
          setLoadingBalances(false);
        }
      }
    },
    {
      enabled: !!partner && !!token && !!organization,
    }
  );

  useEffect(() => {
    if (balanceRefresh) {
      setLoadingBalances(true);
      balancesQuery.refetch();
    }
    if (!seles) {
      fetchBalancesData();
      setSeles(true);
    }
  }, [selectedBalanceTypes, selectedEcheanceClosingDate, balanceRefresh]);

  const countDatesForTypes = (balances, headers) => {
    const counts = headers.reduce((acc, header) => {
      acc[header.label] = 0;
      if (header.children) {
        header.children.forEach((child) => {
          acc[child.label] = 0;
        });
      }
      return acc;
    }, {});

    balances.forEach((balance) => {
      headers.forEach((header) => {
        if (balance[header.value] === "1" || balance[header.value] === "Oui") {
          counts[header.label]++;
        }
        if (header.children) {
          header.children.forEach((child) => {
            if (
              balance[child.value] === "1" ||
              balance[child.value] === "Oui"
            ) {
              counts[child.label]++;
            }
          });
        }
      });
    });
    return counts;
  };
  const fetchBalancesData = async () => {
    try {
      const { closingDate, startDate, endDate } = getDateParams(
        type,
        zoneId,
        selectedEcheanceClosingDate
      );
      if (loggedAs == "MANAGER" && partnerZoneId != null) {
        const responses = await Promise.all(
          balance_types.map((type) =>
            getCollaboratorBalance(
              token,
              organization,
              partner,
              year,
              null,
              collaboratorId,
              zoneId,
              bu,
              type,
              selectedEcheanceClosingDate,
              closingDate,
              null,
              startDate,
              endDate,
              clients
            )
          )
        );
        let updatedPercentageByType = { ...percentageByType };
        responses.forEach((response) => {
          let chekedValue =
            response.data.data.type === "TVA"
              ? response.data.data.response["global_total"]["intervat_deposit"]
              : response.data.data.response["global_total"][
                  "is_bnb_finished_deg"
                ];

          if (response.data.data.type === selectedBalanceTypes) {
            setCountBalances(response.data.data.countData);
            setBalances(response.data.data.response);
            setHeaders(response.data.data.headers);
          }
          updatedPercentageByType[response.data.data.type] = parseFloat(
            (chekedValue / response.data.data.response.length) * 100
          ).toFixed(2);
        });
        setPercentageByType(updatedPercentageByType);
      }
    } catch (error) {
      console.error("Error fetching balances:", error);
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    fetchBalancesData();
  }, [
    token,
    organization,
    partner,
    year,
    collaboratorId,
    zoneId,
    selectedBalanceTypes,
    selectedEcheanceClosingDate,
  ]);

  useEffect(() => {
    if (balanceRefresh) {
      setLoadingBalances(true);
      balancesQuery.refetch();
    }
    if (!seles) {
      balancesQuery.refetch();
      setSeles(true);
    }
  }, [selectedBalanceTypes, selectedEcheanceClosingDate, balanceRefresh]);

  const handleChangeBalanceTypes = (type) => {
    if (onTypeChange) onTypeChange(type);
    setSelectedEcheanceClosingDate(null);
    setSelectedBalanceTypes(type);
    setBalanceRefresh(true);
  };

  const handleChangeBalanceClosingDate = (date) => {
    setSelectedEcheanceClosingDate(date);
    setBalanceRefresh(true);
  };

  const getTvaReportTitle = () => {
    if (
      !selectedEcheanceClosingDate ||
      typeof selectedEcheanceClosingDate !== "object"
    ) {
      return "Rapport TVA";
    }

    const trimesterId = selectedEcheanceClosingDate.id;
    if (!trimesterId) return "Rapport TVA";

    return `Rapport T${trimesterId} - TVA`;
  };

  const reorderedHeaders =
    (selectedBalanceTypes === "ISOC" || selectedBalanceTypes === "IPM") &&
    headers
      ? headers.map((header) => {
          if (header.children && header.children.length === 2) {
            return {
              ...header,
              children: [header.children[1], header.children[0]],
            };
          }
          return header;
        })
      : headers || [];

  const getEcheancesForSelectedType = () => {
    if (selectedBalanceTypes === "TVA") {
      return effectiveZoneId === "2"
        ? ECHEANCES.TVA_MAROC
        : ECHEANCES.TVA_BELGIQUE;
    }
    return ECHEANCES[selectedBalanceTypes] || [];
  };

  const isBelgium = zoneId === "1";
  const effectiveZoneId = String(zoneId);

  return (
    <div className="balances_page">
      <div className="balances">
        Nombre de dossiers : {countBalances}
        <div className="balance_header">
          <div className="types">
            {balance_types.length > 1 &&
              balance_types.map((type) => (
                <>
                  <div
                    className={`item_type ${
                      type === selectedBalanceTypes && "selected_type"
                    } `}
                    onClick={() => handleChangeBalanceTypes(type)}>
                    {type} {"  "}{" "}
                    {/* {(percentageByType[type] && percentageByType[type]) || 0} % */}
                  </div>
                </>
              ))}
          </div>
        </div>
        <div className="echeanches_filter">
          {getEcheancesForSelectedType().map((type) => {
            const isMaroc =
              selectedBalanceTypes === "TVA" && effectiveZoneId === "2";
            const isSelected = isMaroc
              ? type?.label === selectedEcheanceClosingDate?.label
              : type === selectedEcheanceClosingDate;

            const isDisabled = isMaroc && type?.id > moment().quarter();

            return (
              <div
                key={type?.id || type}
                className={`item ${isSelected ? "active" : ""} ${
                  isDisabled ? "disabled" : ""
                }`}
                onClick={() => {
                  if (!isDisabled) handleChangeBalanceClosingDate(type);
                }}
                style={
                  isMaroc && type !== null ? { width: "215px" } : undefined
                }>
                {type ? type.label || type : "Tous"}
              </div>
            );
          })}
        </div>
        {isBelgium ? (
          <>
            {balances && balances.length > 0 && (
              <div className="bilan_table header">
                <div className="bilan_item name" style={{ width: "40%" }}>
                  <div className="content_header"></div>
                </div>

                {headers &&
                  reorderedHeaders.map((header) => (
                    <div className="bilan_item name">
                      <div className="content_header">
                        <div className="sub_title">
                          <div>{header.label}</div>
                          <div className="sub_titles">
                            {header.children &&
                              header.children.length > 0 &&
                              header.children.map((child, index) => (
                                <div
                                  className="sub_title"
                                  style={{
                                    margin: index === 0 && "0 64% 0 0%",
                                  }}>
                                  {child.label}
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                {balances && headers && (
                  <>
                    <div className="bilan_table header">
                      <div className="bilan_item name" style={{ width: "40%" }}>
                        <div className="content_header">Nom du client</div>
                      </div>
                      {headers.map((header, index) => (
                        <div key={index} className="bilan_item name">
                          <div className="content_header">
                            <div className="sub_title">{header.label}</div>
                            {header.children && header.children.length > 0 && (
                              <div className="sub_titles">
                                {header.children.map((child, idx) => (
                                  <div
                                    key={idx}
                                    className="sub_title"
                                    style={{
                                      margin:
                                        idx === 0 ? "0 64% 0 0%" : undefined,
                                    }}>
                                    {child.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bilan_table global_total">
                      <div
                        className="bilan_item name"
                        style={{
                          width: "40%",
                          display: "flex",
                          justifyContent: "flex-start",
                        }}>
                        <div
                          className="content first_content"
                          style={{ paddingLeft: "10px" }}>
                          {balances.global_total.client_name || "Total Global"}
                        </div>
                      </div>
                      {headers.map((header, index) => (
                        <div
                          key={`global-${index}`}
                          className="bilan_item name">
                          <div className="content first_content">
                            {balances.global_total[header.value] || ""}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="group_section">
                      {balances.completed &&
                        balances.completed.map(
                          (bilan, index) =>
                            ((showCompleted && index !== 0) || index === 0) && (
                              <div key={`completed-${index}`}>
                                <div
                                  className="bilan_table"
                                  onClick={() =>
                                    setSelectedClientId(bilan.client_id)
                                  }>
                                  <div
                                    className="bilan_item name"
                                    style={{
                                      width: "40%",
                                      display: "flex",
                                      justifyContent: "flex-start",
                                    }}>
                                    <div
                                      className={`content ${
                                        bilan.id === 0 && "first_content"
                                      }`}
                                      style={{
                                        paddingLeft: "10px",
                                        display: "flex",
                                        justifyContent:
                                          index === 0 && "space-around",
                                        background: index === 0 && "#dee2e6",
                                      }}>
                                      {bilan.client_name}
                                      {index === 0 && (
                                        <div
                                          onClick={() =>
                                            setShowCompleted((prev) => !prev)
                                          }
                                          style={{ cursor: "pointer" }}>
                                          {showCompleted ? (
                                            <ChevronUp />
                                          ) : (
                                            <ChevronDown />
                                          )}
                                        </div>
                                      )}

                                      {index !== 0 && (
                                        <div className="vat_periodicity">
                                          {bilan.vat_periodicity || ""}
                                        </div>
                                      )}
                                      {bilan.follow_up_remarks && "++"}
                                    </div>
                                  </div>
                                  {headers.map((header, indexHeader) => (
                                    <div
                                      key={`completed-${index}-${indexHeader}`}
                                      className="bilan_item name">
                                      <div
                                        className={`content ${
                                          bilan.id === 0 && "first_content"
                                        }`}
                                        style={{
                                          background: index === 0 && "#dee2e6",
                                        }}>
                                        {header.children &&
                                        header.children.length > 0 ? (
                                          header.children.map(
                                            (child, indexChild) => (
                                              <Tooltip
                                                key={`child-${indexChild}`}
                                                title={
                                                  bilan[
                                                    child.value.substring(3) +
                                                      "_date"
                                                  ] && (
                                                    <div>
                                                      <ul>
                                                        {bilan[
                                                          child.value.substring(
                                                            3
                                                          ) + "_date"
                                                        ]
                                                          ? moment(
                                                              bilan[
                                                                child.value.substring(
                                                                  3
                                                                ) + "_date"
                                                              ].date
                                                            ).format(
                                                              "DD/MM/YYYY"
                                                            )
                                                          : " "}
                                                      </ul>
                                                    </div>
                                                  )
                                                }>
                                                <div
                                                  className={`checked_item ${
                                                    bilan.id === 0 &&
                                                    "first_content"
                                                  } ${
                                                    (!bilan[child.value] ||
                                                      bilan[child.value] ===
                                                        "0") &&
                                                    bilan[child.value] !==
                                                      "1" &&
                                                    "unchecked_item"
                                                  }`}
                                                  style={{
                                                    width: "50%",
                                                    background:
                                                      index === 0
                                                        ? "#dee2e6"
                                                        : (!bilan[
                                                            child.value
                                                          ] ||
                                                            bilan[
                                                              child.value
                                                            ] === "0") &&
                                                          bilan[child.value] !==
                                                            "1"
                                                        ? ""
                                                        : indexHeader === 0
                                                        ? "yellow"
                                                        : indexChild === 0
                                                        ? "linear-gradient(to right, yellow, #f7e600)"
                                                        : indexChild === 1
                                                        ? "linear-gradient(to right, #f7e600, #acd46f 50%)"
                                                        : indexHeader === 1
                                                        ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                        : indexHeader === 2
                                                        ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                        : indexHeader === 3
                                                        ? "linear-gradient(to right, #43d18b, #06d9b1)"
                                                        : "linear-gradient(to right, #06d9b1, #00b3f0)",
                                                  }}>
                                                  {bilan.id !== 0 ? (
                                                    bilan[child.value] !==
                                                      "0" &&
                                                    (bilan[child.value] ||
                                                      bilan[child.value] ===
                                                        "1") &&
                                                    (index === 0 ? (
                                                      bilan[child.value]
                                                    ) : (
                                                      <CheckIcon
                                                        fill="#3C4E64"
                                                        width="12"
                                                        height="12"
                                                      />
                                                    ))
                                                  ) : (
                                                    <Tooltip
                                                      title={
                                                        <div>
                                                          <ul>
                                                            (Réalisé / Budgétisé
                                                            ) * 100
                                                          </ul>
                                                        </div>
                                                      }>
                                                      <div>
                                                        {bilan[child.value]}{" "}
                                                        gftft
                                                      </div>
                                                    </Tooltip>
                                                  )}
                                                </div>
                                              </Tooltip>
                                            )
                                          )
                                        ) : (
                                          <Tooltip
                                            title={
                                              bilan[
                                                header.value.substring(3) +
                                                  "_date"
                                              ] && (
                                                <div>
                                                  <ul>
                                                    {bilan[
                                                      header.value.substring(
                                                        3
                                                      ) + "_date"
                                                    ]
                                                      ? moment(
                                                          bilan[
                                                            header.value.substring(
                                                              3
                                                            ) + "_date"
                                                          ].date
                                                        ).format("DD/MM/YYYY")
                                                      : " "}
                                                  </ul>
                                                </div>
                                              )
                                            }>
                                            <div
                                              className={`checked_item ${
                                                bilan.id === 0 &&
                                                "first_content"
                                              } ${
                                                bilan[header.value] !== "0" &&
                                                (bilan[header.value] ||
                                                  bilan[header.value] === "1")
                                                  ? ""
                                                  : "unchecked_item"
                                              }`}
                                              style={{
                                                background:
                                                  index === 0
                                                    ? "#dee2e6"
                                                    : bilan[header.value] !==
                                                        "0" &&
                                                      (bilan[header.value] ||
                                                        bilan[header.value] ===
                                                          "1")
                                                    ? indexHeader === 0
                                                      ? "linear-gradient(to right, yellow, #f7e600)"
                                                      : indexHeader === 1
                                                      ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                      : indexHeader === 2
                                                      ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                      : "linear-gradient(to right, #43d18b, #06d9b1)"
                                                    : "",
                                              }}>
                                              {bilan.id !== 0 ? (
                                                bilan[header.value] !== "0" &&
                                                (bilan[header.value] ||
                                                  bilan[header.value] ===
                                                    "1") &&
                                                (index === 0 ? (
                                                  bilan[header.value]
                                                ) : (
                                                  <CheckIcon
                                                    fill="#3C4E64"
                                                    width="12"
                                                    height="12"
                                                  />
                                                ))
                                              ) : (
                                                <div>{bilan[header.value]}</div>
                                              )}
                                            </div>
                                          </Tooltip>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                        )}

                      <div className="group_section">
                        {balances.not_completed &&
                          balances.not_completed.map(
                            (bilan, index) =>
                              ((showNotCompleted && index !== 0) ||
                                index === 0) && (
                                <div key={`notcompleted-${index}`}>
                                  <div
                                    className="bilan_table"
                                    onClick={() =>
                                      setSelectedClientId(bilan.client_id)
                                    }>
                                    <div
                                      className="bilan_item name"
                                      style={{
                                        width: "40%",
                                        display: "flex",
                                        justifyContent: "flex-start",
                                      }}>
                                      <div
                                        className={`content ${
                                          bilan.id === 0 && "first_content"
                                        }`}
                                        style={{
                                          paddingLeft: "10px",
                                          display: "flex",
                                          justifyContent:
                                            index === 0 && "space-around",
                                          background: index === 0 && "#dee2e6",
                                        }}>
                                        {bilan.client_name}
                                        {index === 0 && (
                                          <div
                                            onClick={() =>
                                              setShowNotCompleted(
                                                (prev) => !prev
                                              )
                                            }
                                            style={{ cursor: "pointer" }}>
                                            {showNotCompleted ? (
                                              <ChevronUp />
                                            ) : (
                                              <ChevronDown />
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {headers.map((header, indexHeader) => (
                                      <div
                                        key={`notcompleted-${index}-${indexHeader}`}
                                        className="bilan_item name">
                                        <div
                                          className={`content ${
                                            bilan.id === 0 && "first_content"
                                          }`}
                                          style={{
                                            background:
                                              index === 0 && "#dee2e6",
                                          }}>
                                          {header.children &&
                                          header.children.length > 0 ? (
                                            header.children.map(
                                              (child, indexChild) => (
                                                <Tooltip
                                                  key={indexChild}
                                                  title={
                                                    bilan[
                                                      child.value.substring(3) +
                                                        "_date"
                                                    ] && (
                                                      <div>
                                                        <ul>
                                                          {bilan[
                                                            child.value.substring(
                                                              3
                                                            ) + "_date"
                                                          ]
                                                            ? moment(
                                                                bilan[
                                                                  child.value.substring(
                                                                    3
                                                                  ) + "_date"
                                                                ].date
                                                              ).format(
                                                                "DD/MM/YYYY"
                                                              )
                                                            : " "}
                                                        </ul>
                                                      </div>
                                                    )
                                                  }>
                                                  <div
                                                    className={`checked_item ${
                                                      bilan.id === 0 &&
                                                      "first_content"
                                                    } ${
                                                      (!bilan[child.value] ||
                                                        bilan[child.value] ===
                                                          "0") &&
                                                      bilan[child.value] !==
                                                        "1" &&
                                                      "unchecked_item"
                                                    }`}
                                                    style={{
                                                      width: "50%",
                                                      background:
                                                        index === 0
                                                          ? "#dee2e6"
                                                          : (!bilan[
                                                              child.value
                                                            ] ||
                                                              bilan[
                                                                child.value
                                                              ] === "0") &&
                                                            bilan[
                                                              child.value
                                                            ] !== "1"
                                                          ? ""
                                                          : indexHeader === 0
                                                          ? "yellow"
                                                          : indexChild === 0
                                                          ? "linear-gradient(to right, yellow, #f7e600)"
                                                          : indexChild === 1
                                                          ? "linear-gradient(to right, #f7e600, #acd46f 50%)"
                                                          : indexHeader === 1
                                                          ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                          : indexHeader === 2
                                                          ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                          : indexHeader === 3
                                                          ? "linear-gradient(to right, #43d18b, #06d9b1)"
                                                          : "linear-gradient(to right, #06d9b1, #00b3f0)",
                                                    }}>
                                                    {bilan.id !== 0 ? (
                                                      bilan[child.value] !==
                                                        "0" &&
                                                      (bilan[child.value] ||
                                                        bilan[child.value] ===
                                                          "1") &&
                                                      (index === 0 ? (
                                                        bilan[child.value]
                                                      ) : (
                                                        <CheckIcon
                                                          fill="#3C4E64"
                                                          width="12"
                                                          height="12"
                                                        />
                                                      ))
                                                    ) : (
                                                      <Tooltip
                                                        title={
                                                          <div>
                                                            <ul>
                                                              (Réalisé /
                                                              Budgétisé ) * 100
                                                            </ul>
                                                          </div>
                                                        }>
                                                        <div>
                                                          {bilan[child.value]}{" "}
                                                          gftft
                                                        </div>
                                                      </Tooltip>
                                                    )}
                                                  </div>
                                                </Tooltip>
                                              )
                                            )
                                          ) : (
                                            <Tooltip
                                              title={
                                                bilan[
                                                  header.value.substring(3) +
                                                    "_date"
                                                ] && (
                                                  <div>
                                                    <ul>
                                                      {bilan[
                                                        header.value.substring(
                                                          3
                                                        ) + "_date"
                                                      ]
                                                        ? moment(
                                                            bilan[
                                                              header.value.substring(
                                                                3
                                                              ) + "_date"
                                                            ].date
                                                          ).format("DD/MM/YYYY")
                                                        : " "}
                                                    </ul>
                                                  </div>
                                                )
                                              }>
                                              <div
                                                className={`checked_item ${
                                                  bilan.id === 0 &&
                                                  "first_content"
                                                } ${
                                                  bilan[header.value] !== "0" &&
                                                  (bilan[header.value] ||
                                                    bilan[header.value] === "1")
                                                    ? ""
                                                    : "unchecked_item"
                                                }`}
                                                style={{
                                                  background:
                                                    index === 0
                                                      ? "#dee2e6"
                                                      : bilan[header.value] !==
                                                          "0" &&
                                                        (bilan[header.value] ||
                                                          bilan[
                                                            header.value
                                                          ] === "1")
                                                      ? indexHeader === 0
                                                        ? "linear-gradient(to right, yellow, #f7e600)"
                                                        : indexHeader === 1
                                                        ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                        : indexHeader === 2
                                                        ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                        : "linear-gradient(to right, #43d18b, #06d9b1)"
                                                      : "",
                                                }}>
                                                {bilan.id !== "0" ? (
                                                  bilan[header.value] !== "0" &&
                                                  (bilan[header.value] ||
                                                    bilan[header.value] ===
                                                      "1") &&
                                                  (index === 0 ? (
                                                    bilan[header.value]
                                                  ) : (
                                                    <CheckIcon
                                                      fill="#3C4E64"
                                                      width="12"
                                                      height="12"
                                                    />
                                                  ))
                                                ) : (
                                                  <div>
                                                    {bilan[header.value]}
                                                  </div>
                                                )}
                                              </div>
                                            </Tooltip>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )
                          )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {selectedBalanceTypes === "TVA" &&
              balances &&
              effectiveZoneId === "2" &&
              (() => {
                const total = balances.global_total.total;

                if (total === 0) {
                  return (
                    <div className="no_data">
                      <div className="title">{_("noDataFound")}</div>
                    </div>
                  );
                }

                const tvaStats = [
                  {
                    label: "Finalisés",
                    value: balances.finalized?.length || 0,
                  },
                  {
                    label: "En cours",
                    value: balances.in_progress?.length || 0,
                  },
                  { label: "Planifiés", value: balances.planned?.length || 0 },
                  {
                    label: "Demande de planification",
                    value: balances.planning_requested?.length || 0,
                  },
                  {
                    label: "À planifier",
                    value: balances.to_plan?.length || 0,
                  },
                  {
                    label: "En attente de documents",
                    value: balances.waiting_documents?.length || 0,
                  },
                  { label: "Annulés", value: balances.cancelled?.length || 0 },
                ];

                const getTotal = (s) =>
                  (s.to_plan || 0) +
                  (s.cancelled || 0) +
                  (s.waiting_documents || 0) +
                  (s.planning_requested || 0) +
                  (s.in_progress || 0) +
                  (s.finalized || 0) +
                  (s.planned || 0);

                const maxTotal = Math.max(
                  ...Object.values(ratesByGE || {}).map(getTotal)
                );
                const minRatio =
                  Math.min(...Object.values(ratesByGE || {}).map(getTotal)) /
                  maxTotal;
                const step = maxTotal <= 5 ? 1 : maxTotal <= 10 ? 2 : 5;
                const tickValues = Array.from(
                  { length: Math.floor(maxTotal / step) + 1 },
                  (_, i) => i * step
                );

                return (
                  <>
                    <div className="tva-summary">
                      <div className="tva-summary-title">
                        {getTvaReportTitle()}
                        {selectedEcheanceClosingDate?.endDate && (
                          <div className="tva-summary-subtitle">
                            Suivi jusqu'au{" "}
                            {moment(
                              `${year}-${selectedEcheanceClosingDate.endDate}`,
                              "YYYY-MM-DD"
                            ).format("DD/MM/YY")}
                          </div>
                        )}
                      </div>
                      <div className="tva-summary-data">
                        {tvaStats.map(({ label, value }, idx) => (
                          <React.Fragment key={label}>
                            <div className="tva-summary-item">
                              <div className="tva-summary-label">{label}</div>
                              <div className="tva-summary-value">{value}</div>
                            </div>
                            {idx < tvaStats.length - 1 && (
                              <div className="tva-summary-divider" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="tva-summary-total">
                        <div className="tva-summary-total-label">Total</div>
                        <div className="tva-summary-total-value">{count}</div>
                      </div>
                    </div>

                    {ratesByGE && (
                      <div className="tva-bar-summary">
                        <div className="tva-summary-container">
                          <div className="tva-summary-box">
                            <div>
                              <strong>
                                Taux d’avancement des dossiers TVA (Finalisés)
                              </strong>
                            </div>
                            <div className="tva-summary-percentage">
                              {percentageByType["TVA"] || 0} %
                            </div>
                          </div>
                          <div className="tva-summary-box">
                            <div>
                              <strong>Taux des dossiers TVA (Restants)</strong>
                            </div>
                            <div className="tva-summary-percentage remaining">
                              {100 - (percentageByType["TVA"] || 0)} %
                            </div>
                          </div>
                        </div>

                        <div className="tva-bar-details">
                          <div className="tva-bar-title">
                            Répartition des dossiers TVA par statut et par GE
                          </div>
                          <div className="tva-bar-container">
                            <div className="tva-bar-graph">
                              {Object.entries(ratesByGE)
                                .sort(
                                  ([, a], [, b]) => getTotal(b) - getTotal(a)
                                )
                                .map(([name, stats], index) => {
                                  const total = getTotal(stats);
                                  const getSegment = (value, colorClass) =>
                                    value > 0 && (
                                      <div
                                        className={`bar-segment ${colorClass}`}
                                        style={{
                                          width: `${(value / total) * 100}%`,
                                        }}>
                                        {value}
                                      </div>
                                    );
                                  return (
                                    <div
                                      key={name}
                                      className="tva-bar-row"
                                      data-index={index}>
                                      <div className="tva-bar-name">{name}</div>
                                      <div className="tva-bar-wrapper">
                                        <div
                                          className="tva-bar"
                                          style={{
                                            width: `${
                                              (total / maxTotal) * 100
                                            }%`,
                                          }}>
                                          {getSegment(
                                            stats.finalized || 0,
                                            "dark-blue"
                                          )}
                                          {getSegment(
                                            stats.in_progress || 0,
                                            "light-blue"
                                          )}
                                          {getSegment(
                                            stats.planned || 0,
                                            "purple"
                                          )}
                                          {getSegment(
                                            stats.planning_requested || 0,
                                            "yellow"
                                          )}
                                          {getSegment(
                                            stats.to_plan || 0,
                                            "gray"
                                          )}
                                          {getSegment(
                                            stats.waiting_documents || 0,
                                            "orange"
                                          )}
                                          {getSegment(
                                            stats.cancelled || 0,
                                            "red"
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}

                              {/* <div className="tva-bar-axis">
                              <div className="tva-bar-name"></div>
                              <div className="tva-bar-axis-line">
                                {tickValues.map((val) => (
                                  <div key={val} className="tva-bar-axis-tick">{val}</div>
                                ))}
                              </div>
                            </div> */}
                            </div>
                          </div>
                        </div>

                        <div className="tva-histogram">
                          <div className="histogram-title">
                            Nombre de dossiers TVA par GE
                          </div>
                          <div className="histogram-bar">
                            <div className="histogram-row">
                              <div className="histogram-name"></div>
                              <div className="histogram-bar-wrapper">
                                <div className="histogram-full-line">
                                  <div className="histogram-percent-label">
                                    100%
                                  </div>
                                  <div className="histogram-line"></div>
                                </div>
                              </div>
                            </div>

                            {Object.entries(ratesByGE)
                              .sort(([, a], [, b]) => getTotal(b) - getTotal(a))
                              .map(([name, stats], index) => {
                                const total = getTotal(stats);
                                return (
                                  <div
                                    key={name}
                                    className="histogram-row"
                                    data-index={index}>
                                    <div className="histogram-name">{name}</div>
                                    <div className="histogram-bar-wrapper">
                                      <div
                                        className="histogram-bar-segment"
                                        style={{
                                          width: `${(total / maxTotal) * 100}%`,
                                        }}>
                                        {total}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                            <div className="histogram-row">
                              <div className="histogram-name"></div>
                              <div className="histogram-bar-wrapper">
                                <div
                                  className="histogram-full-line"
                                  style={{ width: `${minRatio * 100}%` }}>
                                  <div className="histogram-line"></div>
                                  <div className="histogram-percent-label">
                                    {(minRatio * 100).toFixed(1)}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="tva-legend-fixed">
                      <div className="tva-bar-legend">
                        <div className="legend-item">
                          <div className="legend-color dark-blue"></div>
                          Finalisés
                        </div>
                        <div className="legend-item">
                          <div className="legend-color light-blue"></div>En
                          cours
                        </div>
                        <div className="legend-item">
                          <div className="legend-color purple"></div>Planifiés
                        </div>
                        <div className="legend-item">
                          <div className="legend-color yellow"></div>Demande de
                          planification
                        </div>
                        <div className="legend-item">
                          <div className="legend-color gray"></div>À planifier
                        </div>
                        <div className="legend-item">
                          <div className="legend-color orange"></div>En attente
                          de documents
                        </div>
                        <div className="legend-item">
                          <div className="legend-color red"></div>Annulés
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
          </>
        )}
        {loadingBalances || fetchBalancesData.isFetching ? (
          <div className="ripple_loading">
            <img src={"/img/Ripple-1s-150px.svg"} alt="" />
          </div>
        ) : (
          <div className="table">
            {balances && balances.length === 0 && (
              <div className="no_data" style={{ height: 38 }}>
                <div className="title">{_("Oops")}</div>
                <div className="subTitle">{_("noBalanceFound")}</div>
              </div>
            )}
            {balances && balances.length > 0 && (
              <div className="bilan_table header">
                <div className="bilan_item name" style={{ width: "40%" }}>
                  <div className="content_header"></div>
                </div>

                {headers &&
                  reorderedHeaders.map((header) => (
                    <div className="bilan_item name">
                      <div className="content_header">
                        <div className="sub_title">
                          <div>{header.label}</div>
                          <div className="sub_titles">
                            {header.children &&
                              header.children.length > 0 &&
                              header.children.map((child, index) => (
                                <div
                                  className="sub_title"
                                  style={{
                                    margin: index === 0 && "0 64% 0 0%",
                                  }}>
                                  {child.label}
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {balances && headers && (
              <>
                <div className="bilan_table header">
                  <div className="bilan_item name" style={{ width: "40%" }}>
                    <div className="content_header">Nom du client</div>
                  </div>
                  {reorderedHeaders.map((header, index) => (
                    <div key={index} className="bilan_item name">
                      <div className="content_header">
                        <div className="sub_title">{header.label}</div>
                        {header.children && header.children.length > 0 && (
                          <div className="sub_titles">
                            {header.children.map((child, idx) => (
                              <div
                                key={idx}
                                className="sub_title"
                                style={{
                                  margin: idx === 0 ? "0 64% 0 0%" : undefined,
                                }}>
                                {child.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bilan_table global_total">
                  <div
                    className="bilan_item name"
                    style={{
                      width: "40%",
                      display: "flex",
                      justifyContent: "flex-start",
                    }}>
                    <div
                      className="content first_content"
                      style={{ paddingLeft: "10px" }}>
                      {balances.global_total.client_name || "Total Global"}
                    </div>
                  </div>
                  {reorderedHeaders.map((header, index) => (
                    <div key={`global-${index}`} className="bilan_item name">
                      <div className="content first_content">
                        {balances.global_total[header.value] || ""}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="group_section">
                  {balances.completed &&
                    balances.completed.map(
                      (bilan, index) =>
                        ((showCompleted && index !== 0) || index === 0) && (
                          <div key={`completed-${index}`}>
                            <div
                              className="bilan_table"
                              onClick={() =>
                                setSelectedClientId(bilan.client_id)
                              }>
                              <div
                                className="bilan_item name"
                                style={{
                                  width: "40%",
                                  display: "flex",
                                  justifyContent: "flex-start",
                                }}>
                                <div
                                  className={`content ${
                                    bilan.id === 0 ? "first_content" : ""
                                  }`}
                                  style={{
                                    paddingLeft: "10px",
                                    display: "flex",
                                    justifyContent:
                                      index === 0 && "space-around",
                                    background: index === 0 && "#dee2e6",
                                  }}>
                                  <div className="foldable_section_title">
                                    {bilan.client_name}
                                    {bilan.client_name ===
                                      "Total Completed" && (
                                      <span>({bilan.total})</span>
                                    )}
                                  </div>
                                  {index === 0 && (
                                    <div
                                      onClick={() =>
                                        setShowCompleted((prev) => !prev)
                                      }
                                      style={{ cursor: "pointer" }}>
                                      {showCompleted ? (
                                        <ChevronUp />
                                      ) : (
                                        <ChevronDown />
                                      )}
                                    </div>
                                  )}

                                  {index !== 0 && (
                                    <div className="vat_periodicity">
                                      {bilan.vat_periodicity || ""}
                                    </div>
                                  )}
                                  {bilan.follow_up_remarks && "++"}
                                </div>
                              </div>
                              {reorderedHeaders.map((header, indexHeader) => (
                                <div
                                  key={`completed-${index}-${indexHeader}`}
                                  className="bilan_item name">
                                  <div
                                    className={`content ${
                                      bilan.id === 0 && "first_content"
                                    }`}
                                    style={{
                                      background: index === 0 && "#dee2e6",
                                    }}>
                                    {header.children &&
                                    header.children.length > 0 ? (
                                      header.children.map(
                                        (child, indexChild) => (
                                          <Tooltip
                                            key={`child-${indexChild}`}
                                            title={
                                              bilan[
                                                child.value.substring(3) +
                                                  "_date"
                                              ] && (
                                                <div>
                                                  <ul>
                                                    {bilan[
                                                      child.value.substring(3) +
                                                        "_date"
                                                    ]
                                                      ? moment(
                                                          bilan[
                                                            child.value.substring(
                                                              3
                                                            ) + "_date"
                                                          ].date
                                                        ).format("DD/MM/YYYY")
                                                      : " "}
                                                  </ul>
                                                </div>
                                              )
                                            }>
                                            <div
                                              className={`checked_item ${
                                                bilan.id === 0 &&
                                                "first_content"
                                              } ${
                                                (!bilan[child.value] ||
                                                  bilan[child.value] === "0") &&
                                                bilan[child.value] !== "1" &&
                                                "unchecked_item"
                                              }`}
                                              style={{
                                                width: "50%",
                                                background:
                                                  index === 0
                                                    ? "#dee2e6"
                                                    : (!bilan[child.value] ||
                                                        bilan[child.value] ===
                                                          "0") &&
                                                      bilan[child.value] !== "1"
                                                    ? ""
                                                    : indexHeader === 0
                                                    ? "yellow"
                                                    : indexChild === 0
                                                    ? "linear-gradient(to right, yellow, #f7e600)"
                                                    : indexChild === 1
                                                    ? "linear-gradient(to right, #f7e600, #acd46f 50%)"
                                                    : indexHeader === 1
                                                    ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                    : indexHeader === 2
                                                    ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                    : indexHeader === 3
                                                    ? "linear-gradient(to right, #43d18b, #06d9b1)"
                                                    : "linear-gradient(to right, #06d9b1, #00b3f0)",
                                              }}>
                                              {bilan.id !== 0 ? (
                                                bilan[child.value] !== "0" &&
                                                (bilan[child.value] ||
                                                  bilan[child.value] === "1") &&
                                                (index === 0 ? (
                                                  selectedBalanceTypes ===
                                                    "ISOC" ||
                                                  selectedBalanceTypes ===
                                                    "IPM" ? (
                                                    `${
                                                      bilan[child.value] || 0
                                                    } / ${
                                                      totalByField[
                                                        child.value
                                                      ] || 0
                                                    }`
                                                  ) : (
                                                    `${bilan[child.value] || 0}`
                                                  )
                                                ) : (
                                                  <CheckIcon
                                                    fill="#3C4E64"
                                                    width="12"
                                                    height="12"
                                                  />
                                                ))
                                              ) : (
                                                <Tooltip
                                                  title={
                                                    <div>
                                                      <ul>
                                                        (Réalisé / Budgétisé ) *
                                                        100
                                                      </ul>
                                                    </div>
                                                  }>
                                                  <div>
                                                    {bilan[child.value]} gftft
                                                  </div>
                                                </Tooltip>
                                              )}
                                            </div>
                                          </Tooltip>
                                        )
                                      )
                                    ) : (
                                      <Tooltip
                                        title={
                                          bilan[
                                            header.value.substring(3) + "_date"
                                          ] && (
                                            <div>
                                              <ul>
                                                {bilan[
                                                  header.value.substring(3) +
                                                    "_date"
                                                ]
                                                  ? moment(
                                                      bilan[
                                                        header.value.substring(
                                                          3
                                                        ) + "_date"
                                                      ].date
                                                    ).format("DD/MM/YYYY")
                                                  : " "}
                                              </ul>
                                            </div>
                                          )
                                        }>
                                        <div
                                          className={`checked_item ${
                                            bilan.id === 0 && "first_content"
                                          } ${
                                            bilan[header.value] !== "0" &&
                                            (bilan[header.value] ||
                                              bilan[header.value] === "1")
                                              ? ""
                                              : "unchecked_item"
                                          }`}
                                          style={{
                                            background:
                                              index === 0
                                                ? "#dee2e6"
                                                : bilan[header.value] !== "0" &&
                                                  (bilan[header.value] ||
                                                    bilan[header.value] === "1")
                                                ? indexHeader === 0
                                                  ? "linear-gradient(to right, yellow, #f7e600)"
                                                  : indexHeader === 1
                                                  ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                  : indexHeader === 2
                                                  ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                  : "linear-gradient(to right, #43d18b, #06d9b1)"
                                                : "",
                                          }}>
                                          {bilan.id !== 0 ? (
                                            bilan[header.value] !== "0" &&
                                            (bilan[header.value] ||
                                              bilan[header.value] === "1") &&
                                            (index === 0 ? (
                                              selectedBalanceTypes === "ISOC" ||
                                              selectedBalanceTypes === "IPM" ? (
                                                `${
                                                  bilan[header.value] || 0
                                                } / ${
                                                  totalByField[header.value] ||
                                                  0
                                                }`
                                              ) : (
                                                `${bilan[header.value] || 0}`
                                              )
                                            ) : (
                                              <CheckIcon
                                                fill="#3C4E64"
                                                width="12"
                                                height="12"
                                              />
                                            ))
                                          ) : (
                                            <div>{bilan[header.value]}</div>
                                          )}
                                        </div>
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                    )}

                  <div className="group_section">
                    {balances.not_completed &&
                      balances.not_completed.map(
                        (bilan, index) =>
                          ((showNotCompleted && index !== 0) ||
                            index === 0) && (
                            <div key={`notcompleted-${index}`}>
                              <div
                                className="bilan_table"
                                onClick={() =>
                                  setSelectedClientId(bilan.client_id)
                                }>
                                <div
                                  className="bilan_item name"
                                  style={{
                                    width: "40%",
                                    display: "flex",
                                    justifyContent: "flex-start",
                                  }}>
                                  <div
                                    className={`content ${
                                      bilan.id === 0 ? "first_content" : ""
                                    }`}
                                    style={{
                                      paddingLeft: "10px",
                                      display: "flex",
                                      justifyContent:
                                        index === 0 && "space-around",
                                      background: index === 0 && "#dee2e6",
                                    }}>
                                    <div className="foldable_section_title">
                                      {bilan.client_name}
                                      {bilan.client_name ===
                                        "Total Not Completed" && (
                                        <span>({bilan.total})</span>
                                      )}
                                    </div>
                                    {index === 0 && (
                                      <div
                                        onClick={() =>
                                          setShowNotCompleted((prev) => !prev)
                                        }
                                        style={{ cursor: "pointer" }}>
                                        {showNotCompleted ? (
                                          <ChevronUp />
                                        ) : (
                                          <ChevronDown />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {reorderedHeaders.map((header, indexHeader) => (
                                  <div
                                    key={`notcompleted-${index}-${indexHeader}`}
                                    className="bilan_item name">
                                    <div
                                      className={`content ${
                                        bilan.id === 0 && "first_content"
                                      }`}
                                      style={{
                                        background: index === 0 && "#dee2e6",
                                      }}>
                                      {header.children &&
                                      header.children.length > 0 ? (
                                        header.children.map(
                                          (child, indexChild) => (
                                            <Tooltip
                                              key={indexChild}
                                              title={
                                                bilan[
                                                  child.value.substring(3) +
                                                    "_date"
                                                ] && (
                                                  <div>
                                                    <ul>
                                                      {bilan[
                                                        child.value.substring(
                                                          3
                                                        ) + "_date"
                                                      ]
                                                        ? moment(
                                                            bilan[
                                                              child.value.substring(
                                                                3
                                                              ) + "_date"
                                                            ].date
                                                          ).format("DD/MM/YYYY")
                                                        : " "}
                                                    </ul>
                                                  </div>
                                                )
                                              }>
                                              <div
                                                className={`checked_item ${
                                                  bilan.id === 0 &&
                                                  "first_content"
                                                } ${
                                                  (!bilan[child.value] ||
                                                    bilan[child.value] ===
                                                      "0") &&
                                                  bilan[child.value] !== "1" &&
                                                  "unchecked_item"
                                                }`}
                                                style={{
                                                  width: "50%",
                                                  background:
                                                    index === 0
                                                      ? "#dee2e6"
                                                      : (!bilan[child.value] ||
                                                          bilan[child.value] ===
                                                            "0") &&
                                                        bilan[child.value] !==
                                                          "1"
                                                      ? ""
                                                      : indexHeader === 0
                                                      ? "yellow"
                                                      : indexChild === 0
                                                      ? "linear-gradient(to right, yellow, #f7e600)"
                                                      : indexChild === 1
                                                      ? "linear-gradient(to right, #f7e600, #acd46f 50%)"
                                                      : indexHeader === 1
                                                      ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                      : indexHeader === 2
                                                      ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                      : indexHeader === 3
                                                      ? "linear-gradient(to right, #43d18b, #06d9b1)"
                                                      : "linear-gradient(to right, #06d9b1, #00b3f0)",
                                                }}>
                                                {bilan.id !== 0 ? (
                                                  bilan[child.value] !== "0" &&
                                                  (bilan[child.value] ||
                                                    bilan[child.value] ===
                                                      "1") &&
                                                  (index === 0 ? (
                                                    selectedBalanceTypes ===
                                                      "ISOC" ||
                                                    selectedBalanceTypes ===
                                                      "IPM" ? (
                                                      `${
                                                        bilan[child.value] || 0
                                                      } / ${
                                                        totalByField[
                                                          child.value
                                                        ] || 0
                                                      }`
                                                    ) : (
                                                      `${
                                                        bilan[child.value] || 0
                                                      }`
                                                    )
                                                  ) : (
                                                    <CheckIcon
                                                      fill="#3C4E64"
                                                      width="12"
                                                      height="12"
                                                    />
                                                  ))
                                                ) : (
                                                  <Tooltip
                                                    title={
                                                      <div>
                                                        <ul>
                                                          (Réalisé / Budgétisé )
                                                          * 100
                                                        </ul>
                                                      </div>
                                                    }>
                                                    <div>
                                                      {bilan[child.value]} gftft
                                                    </div>
                                                  </Tooltip>
                                                )}
                                              </div>
                                            </Tooltip>
                                          )
                                        )
                                      ) : (
                                        <Tooltip
                                          title={
                                            bilan[
                                              header.value.substring(3) +
                                                "_date"
                                            ] && (
                                              <div>
                                                <ul>
                                                  {bilan[
                                                    header.value.substring(3) +
                                                      "_date"
                                                  ]
                                                    ? moment(
                                                        bilan[
                                                          header.value.substring(
                                                            3
                                                          ) + "_date"
                                                        ].date
                                                      ).format("DD/MM/YYYY")
                                                    : " "}
                                                </ul>
                                              </div>
                                            )
                                          }>
                                          <div
                                            className={`checked_item ${
                                              bilan.id === 0 && "first_content"
                                            } ${
                                              bilan[header.value] !== "0" &&
                                              (bilan[header.value] ||
                                                bilan[header.value] === "1")
                                                ? ""
                                                : "unchecked_item"
                                            }`}
                                            style={{
                                              background:
                                                index === 0
                                                  ? "#dee2e6"
                                                  : bilan[header.value] !==
                                                      "0" &&
                                                    (bilan[header.value] ||
                                                      bilan[header.value] ===
                                                        "1")
                                                  ? indexHeader === 0
                                                    ? "linear-gradient(to right, yellow, #f7e600)"
                                                    : indexHeader === 1
                                                    ? "linear-gradient(to right, #f7e600, #acd46f)"
                                                    : indexHeader === 2
                                                    ? "linear-gradient(to right, #acd46f, #43d18b)"
                                                    : "linear-gradient(to right, #43d18b, #06d9b1)"
                                                  : "",
                                            }}>
                                            {bilan.id !== "0" ? (
                                              bilan[header.value] !== "0" &&
                                              (bilan[header.value] ||
                                                bilan[header.value] === "1") &&
                                              (index === 0 ? (
                                                selectedBalanceTypes ===
                                                  "ISOC" ||
                                                selectedBalanceTypes ===
                                                  "IPM" ? (
                                                  `${
                                                    bilan[header.value] || 0
                                                  } / ${
                                                    totalByField[
                                                      header.value
                                                    ] || 0
                                                  }`
                                                ) : (
                                                  `${bilan[header.value] || 0}`
                                                )
                                              ) : (
                                                <CheckIcon
                                                  fill="#3C4E64"
                                                  width="12"
                                                  height="12"
                                                />
                                              ))
                                            ) : (
                                              <div>{bilan[header.value]}</div>
                                            )}
                                          </div>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                      )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;
